import classnames from 'classnames';
import _ from 'lodash';
import type React from 'react';
import Select, { components } from 'react-select';
import { Button, UncontrolledTooltip } from 'reactstrap';
import addIcon from '@/shared/ui/icons/Components/Actions/colored-add-icon.svg';
import darkArrowDownIcon from '@/shared/ui/icons/Components/dark-arrow-down-icon.svg';
import lightArrowDownIcon from '@/shared/ui/icons/Components/light-arrow-down-icon.svg';
import useSelectInput from './useSelectInput';

export type SelectInputOption = {
  label?: string;
  value?: string | number;
  isAddButton?: boolean;
  id?: string;
  hasTooltip?: boolean;
  tooltipText?: React.ReactNode;
  [key: string]: any;
};

export type SelectInputProps = {
  name: string;
  label?: string;
  className?: string;
  disabled?: boolean;
  isMulti?: boolean;
  isClearable?: boolean;
  isSearchable?: boolean;
  hasNoBorder?: boolean;
  placeholder?: string;
  isError?: boolean;
  errorMessage?: string;
  options: SelectInputOption[];
  value?: SelectInputOption | SelectInputOption[];
  onChange: (
    name: string,
    value: SelectInputOption | SelectInputOption[] | null,
    index?: number,
  ) => void;
  handleClickAdd?: () => void;
  isWarning?: boolean;
  warningMessage?: string;
  linkText?: string;
  handleClickLinkText?: (e: React.MouseEvent) => void;
  id?: string;
  classNameLabel?: string;
  onKeyDown?: (e: React.KeyboardEvent) => void;
  dropdownIndicatorComponent?: React.ReactNode;
  isHideDefaultDropdownIndicator?: boolean;
  optionCheckOut?: React.ReactNode;
  messageBoxContent?: React.ReactNode;
  holterAndFollowOnContent?: React.ReactNode;
  index?: number;
  additionalText?: string;
  classNamePrefix?: string;
  cautionText?: React.ReactNode;
  tooltipId?: string;
  tooltipMessage?: React.ReactNode;
  // default value below returns void, not boolean — kept as-is (pre-existing).
  isOptionDisabled?: (option: SelectInputOption) => any;
  menuPlacement?: 'auto' | 'bottom' | 'top';
  forceCloseMenu?: boolean;
  couldForceCloseMenu?: boolean;
  hasNoBoxShadow?: boolean;
  containerId?: string;
  // used in JSX below but never declared in the original PropTypes
  isUnsetMinWidth?: boolean;
  blurInputOnSelect?: boolean;
  ariaLabel?: string;
};

const SelectInput = ({
  name,
  label = '',
  className = '',
  disabled = false,
  isMulti = false,
  isClearable = false,
  isSearchable = false,
  hasNoBorder = false,
  placeholder = '',
  isError = false,
  errorMessage = '',
  options,
  value,
  onChange,
  handleClickAdd = () => {},
  isWarning = false,
  warningMessage = '',
  linkText = '',
  handleClickLinkText = () => {},
  id = '',
  classNameLabel = '',
  onKeyDown: onKeyDownProp = () => {},
  dropdownIndicatorComponent,
  isHideDefaultDropdownIndicator = false,
  optionCheckOut,
  messageBoxContent = null,
  holterAndFollowOnContent = null,
  index,
  additionalText = '',
  classNamePrefix,
  cautionText = null,
  tooltipId = '',
  tooltipMessage = '',
  isOptionDisabled = () => {},
  menuPlacement = 'bottom',
  forceCloseMenu = false,
  couldForceCloseMenu,
  hasNoBoxShadow = false,
  containerId,
  isUnsetMinWidth,
  blurInputOnSelect,
  ariaLabel,
}: SelectInputProps) => {
  const {
    selectInputRef,
    menuIsOpen,
    onSelectMenuOpen,
    onSelectMenuClose,
    handleChange,
    onClickAdd,
    onKeyDown,
  } = useSelectInput({
    name,
    onChange,
    index,
    handleClickAdd,
    onKeyDown: onKeyDownProp,
    forceCloseMenu,
    couldForceCloseMenu,
  });
  // No htmlFor/label association here (would make the label text
  // click-to-focus the select, a UI behaviour change) — the visible label
  // doubles as the accessible name via aria-label instead.
  const resolvedAriaLabel = ariaLabel || label || undefined;

  /**
   * Produces the react-select style overrides for the shared select input. Returns per-slot functions sizing the control, switching border, box shadow and padding on the isError, hasNoBorder, hasNoBoxShadow and isClearable flags, greying the disabled background, hiding the indicator separator and the clear indicator when a custom dropdown indicator is given, and colouring the teal multi-value chips and hovered options.
   *
   * @returns An object of react-select style callbacks keyed by slot name.
   */
  const generateCustomStyles = () => ({
    container: (provided: any, state: any) => ({
      ...provided,
      minWidth: isUnsetMinWidth ? 'unset' : '9.375rem',
      pointerEvents: 'all',
    }),
    control: (provided: any, state: any) => ({
      ...provided,
      cursor: state.isDisabled ? 'not-allowed' : 'auto',
      minHeight: isMulti ? '2rem' : 'unset',
      height: isMulti ? 'unset' : '2rem',
      boxShadow: hasNoBoxShadow
        ? 'none'
        : hasNoBorder
          ? 'none'
          : state.isFocused
            ? '0px 0px 0px 2px #7fd0d5'
            : 'none',
      border: hasNoBorder ? 'none' : isError ? '1px solid #ff5a5f' : '1px solid #c6cbd1',
      borderRadius: '4px',
      padding: hasNoBorder ? 'unset' : isClearable ? '0 0 0 1rem' : '0 1rem',
      backgroundColor: state.isDisabled ? '#f5f7fa' : 'white',

      '&:hover': {
        border: hasNoBorder ? 'none' : isError ? '1px solid #ff5a5f' : '1px solid #c6cbd1',
      },
    }),
    indicatorSeparator: (provided: any, state: any) => ({
      ...provided,
      display: 'none',

      "+ div[class*='-indicatorContainer']": {
        padding: 0,
      },
    }),
    clearIndicator: (provided: any, state: any) => ({
      ...provided,
      display: dropdownIndicatorComponent ? 'none' : 'initial',
    }),
    menu: (provided: any, state: any) => ({
      ...provided,
      boxShadow: '0px 6px 12px rgba(29, 47, 71, 0.06)',
    }),
    multiValue: (provided: any, state: any) => ({
      ...provided,
      alignItems: 'center',
      height: isMulti ? '1.5rem' : 'unset',
      backgroundColor: '#0d9e92',
      borderRadius: '4px',
      color: 'white',
      padding: '0 0.5rem',
    }),
    multiValueLabel: (provided: any, state: any) => ({
      ...provided,
      color: 'white',
      padding: 0,
      paddingLeft: 0,
      fontSize: '0.875rem',
    }),
    multiValueRemove: (provided: any, state: any) => ({
      ...provided,
      padding: 0,
      marginLeft: '0.75rem',

      '&:hover': {
        backgroundColor: '#0d9e92',
        color: 'white',
        cursor: 'pointer',
      },
    }),
    option: (provided: any, state: any) => ({
      ...provided,
      backgroundColor: state.isFocused ? '#edf9fa' : 'white',
      color: state.isDisabled ? '#828C99' : state.isSelected ? '#1d2f47' : '#4b5b73',
      fontWeight: state.isSelected ? 'bold' : 'normal',

      '&:hover': {
        backgroundColor: '#edf9fa',
        cursor: state.isDisabled ? 'not-allowed' : 'pointer',
      },

      '&:not(:first-of-type)': {
        borderTop: '1px solid #f5f7fa',
      },
    }),
    placeholder: (provided: any, state: any) => ({
      ...provided,
      color: '#828c99',
      fontStyle: 'italic',
    }),
    singleValue: (provided: any, state: any) => ({
      ...provided,
      color: state.isDisabled ? '#4B5B73' : '#1d2f47',
    }),
    valueContainer: (provided: any, state: any) => ({
      ...provided,
      maxHeight: 'calc(8.25rem - 2 * 0.75rem)',
      padding: 0,
    }),
  });

  /**
   * Copies the current select value so its labels can be reassigned safely. Deep clones the value and, for a multi select, reassigns the label of every entry that has one, otherwise reassigns the single value's label.
   *
   * @param value - The currently selected option or options.
   * @param isMulti - Whether the select holds several values.
   * @returns The cloned selected value.
   */
  const getTextValue = (
    value: SelectInputOption | SelectInputOption[] | undefined,
    isMulti: boolean,
  ) => {
    const newValue: any = _.cloneDeep(value);
    if (isMulti) {
      _.forEach(newValue, (item) => {
        if (item?.label) {
          _.assign(item, { label: item.label });
        }
      });
    } else if (newValue?.label) {
      _.assign(newValue, { label: newValue.label });
    }
    return newValue;
  };

  /**
   * Copies the select options so their labels can be reassigned without touching the caller's array. Deep clones the list with lodash and writes each option's own label back onto the clone.
   *
   * @param options - The select options to clone.
   * @returns The cloned option list.
   */
  const getTextOptions = (options: SelectInputOption[]) => {
    const newOptions = _.cloneDeep(options);
    _.forEach(newOptions, (option) => {
      _.assign(option, { label: option.label });
    });
    return newOptions;
  };

  // react-select's `components` prop is expected to be a plain object of component
  // definitions passed fresh each render (its own docs recommend this); not a case
  // of the usual unstable-nested-component remount concern.
  /* eslint-disable react/no-unstable-nested-components */
  /**
   * Builds the react-select component overrides used by the shared select input. Wraps Control in a tooltip anchor when tooltipId is set, replaces DropdownIndicator with the caller's node or a light or dark arrow icon chosen by hasValue unless indicators are hidden, and renders Option either as an add button calling onClickAdd or as the default option optionally followed by a tooltip.
   *
   * @param dropdownIndicatorComponent - Custom node rendered in place of the default arrow indicator.
   * @param isHideDefaultDropdownIndicator - When true and no custom node is given, no indicator is rendered.
   * @returns An object carrying the Control, DropdownIndicator and Option overrides.
   */
  const generateSelectInputComponents = (
    dropdownIndicatorComponent: React.ReactNode,
    isHideDefaultDropdownIndicator: boolean,
  ) => {
    const inputComponents = {
      Control: ({ children, ...rest }: any) =>
        tooltipId ? (
          <div id={tooltipId}>
            {/* eslint-disable-next-line react/jsx-props-no-spreading -- react-select's
              own customization API forwards its internal render-prop bag this way */}
            <components.Control {...rest}>{children}</components.Control>
            <UncontrolledTooltip
              className="custom-tooltip"
              target={tooltipId}
              placement="bottom"
              delay={{ show: 0, hide: 0 }}
              hideArrow
            >
              {tooltipMessage}
            </UncontrolledTooltip>
          </div>
        ) : (
          // eslint-disable-next-line react/jsx-props-no-spreading -- see above
          <components.Control {...rest}>{children}</components.Control>
        ),
      DropdownIndicator: (selectProps: any) =>
        dropdownIndicatorComponent ||
        (isHideDefaultDropdownIndicator ? null : (
          <img
            style={{ marginBottom: '5px' }}
            src={selectProps.hasValue ? darkArrowDownIcon : lightArrowDownIcon}
            alt="Arrow down icon"
          />
        )), // *: Using search icon (if has no value) and default clear icon (if has value)
      // *: Use default clear icon to prevent keep opening menu after clear value
      // Input: selectProps => <components.Input {...selectProps} />,
      Option: (selectProps: any) => {
        if (selectProps.data.isAddButton) {
          return (
            <div className="add-btn">
              <Button onClick={onClickAdd}>
                <img src={addIcon} alt="Create new referring physician icon" />
                <span className="text">{selectProps.label}</span>
              </Button>
            </div>
          );
        }
        return (
          <>
            <div id={selectProps.data.id}>
              {/* eslint-disable-next-line react/jsx-props-no-spreading -- see above */}
              <components.Option {...selectProps} />
            </div>
            {selectProps.data.hasTooltip && (
              <UncontrolledTooltip
                className="custom-tooltip"
                target={selectProps.data.id}
                placement="bottom-end"
                delay={{ show: 0, hide: 0 }}
                hideArrow
                modifiers={{ preventOverflow: { padding: -50 } }}
              >
                {selectProps.data.tooltipText}
              </UncontrolledTooltip>
            )}
          </>
        );
      },
    };
    return inputComponents;
  };
  /* eslint-enable react/no-unstable-nested-components */

  return (
    <div className={classnames('custom-select-input', className)}>
      {!!messageBoxContent && messageBoxContent}
      {!!label && (
        <div className={classnames('custom-select-input__label', classNameLabel)}>{label}</div>
      )}
      {optionCheckOut}
      <Select<SelectInputOption, boolean>
        ref={selectInputRef}
        inputId={id}
        aria-label={resolvedAriaLabel}
        id={containerId}
        onKeyDown={onKeyDown}
        menuIsOpen={menuIsOpen}
        onMenuOpen={onSelectMenuOpen}
        onMenuClose={onSelectMenuClose}
        isDisabled={disabled}
        styles={generateCustomStyles() as any}
        value={getTextValue(value, isMulti)}
        onChange={handleChange as any}
        options={getTextOptions(options)}
        isMulti={isMulti}
        isClearable={isClearable}
        isSearchable={isSearchable}
        placeholder={placeholder}
        closeMenuOnSelect={!isMulti}
        components={
          generateSelectInputComponents(
            dropdownIndicatorComponent,
            isHideDefaultDropdownIndicator,
          ) as any
        }
        className={isError ? '--error' : ''}
        classNamePrefix={classNamePrefix}
        isOptionDisabled={isOptionDisabled as any}
        menuPlacement={menuPlacement}
        blurInputOnSelect={blurInputOnSelect}
      />

      {!!holterAndFollowOnContent && holterAndFollowOnContent}

      {isError && !!errorMessage ? (
        <div className="custom-select-input__error-message">{errorMessage}</div>
      ) : null}

      {additionalText ? <div className="checking-device-text">{additionalText}</div> : null}

      {!!cautionText && cautionText}

      {isWarning && (
        <div className="custom-select-input__warning-container">
          <div className="custom-select-input__warning-message">{warningMessage}</div>
          <Button
            className="custom-select-input__warning-button"
            color="link"
            onClick={handleClickLinkText}
          >
            {linkText}
          </Button>
        </div>
      )}
    </div>
  );
};

export default SelectInput;
